import { Fragment, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useChatStore, type StreamingMessage } from '../../../store/chatStore';
import { useAuthStore } from '../../../store/authStore';
import { isNearBottom } from '../../../hooks/useAutoScroll';
import { isSystemMessage } from '../../../lib/chat-message';
import type { ChatMessageEvent, RoomMemberDto } from '../../../types/chat';
import { MessageItem } from './MessageItem';
import { AiThinkingMessage } from './AiThinkingMessage';
import { AiStreamingMessage } from './AiStreamingMessage';
import { ChevronDown } from '../../../icons';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';
// chat-display-names — 말풍선 위 이름도 내가 부르는 이름을 따른다(한 화면에서 이름이 갈리지 않게).
import { useChatDisplayNameStore, myNameFor } from '../../../store/chatDisplayNameStore';
import { avatarImageUrl } from '../../../lib/avatar-src';
import {
  countUnreadMembers,
  findFirstUnreadIndex,
  isDirectRoom,
  listUnreadUserIds,
} from '../../../lib/chat-read';

interface MessageListProps {
  roomId: string;
  members: RoomMemberDto[];
  /** 상단 '이전 대화 불러오기' 클릭 핸들러 (ChatPage 의 cursor 로드). */
  onLoadOlder?: (roomId: string) => void;
  /** chat-read-receipts — 1:1('읽음') vs 그룹('모두 읽음'·미읽음 인원 N) 문구 분기용. */
  roomType?: string | null;
  /** chat-read-receipts — 활성 멤버 총원. 2명이면 사실상 1:1 로 취급. */
  memberCount?: number;
}

/**
 * MessageList (P2-16c) — 시안 line 1155~1209.
 *
 * 책임:
 *   - chatStore.messagesByRoom[roomId] 를 시간순 렌더
 *   - useAutoScroll 적용 (P2-15a — 임계 120px)
 *   - 같은 sender 연속 메시지는 avatar 숨김 (showAvatar grouping)
 *   - 일자 구분선 (오늘/어제/MM월 DD일) — 첫 메시지 일자만 표시 (코어 골격)
 *
 * AI 카드 (P2-16d/e/f/g/h) 는 deferred — MessageItem 이 placeholder 렌더.
 */
export function MessageList({ roomId, members, onLoadOlder, roomType, memberCount }: MessageListProps) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  const messages = useChatStore((s) => s.messagesByRoom[roomId] ?? EMPTY);
  const streamingMap = useChatStore((s) => s.streamingByRoom[roomId]);
  const aiPending = useChatStore((s) => s.aiPendingByRoom[roomId] ?? false);
  const aiTool = useChatStore((s) => s.aiToolByRoom[roomId] ?? null);
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  // 이전(과거) 메시지: cursor 가 string 이면 더 있음 → 상단 버튼 노출.
  const historyCursor = useChatStore((s) => s.historyCursorByRoom[roomId]);
  const loadingOlder = useChatStore((s) => s.loadingOlderByRoom[roomId] ?? false);
  const hasMoreHistory = typeof historyCursor === 'string';
  const containerRef = useRef<HTMLDivElement>(null);

  // "최신 메시지로 이동" 화살표 — 사용자가 위로 스크롤(visual bottom 에서 벗어남)했을 때만 노출.
  // column-reverse 컨테이너: scrollTop = 0 이 visual bottom, 양수면 위로 스크롤 상태.
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const handleScroll = useCallback(() => {
    setShowJumpToBottom(!isNearBottom(containerRef.current));
  }, []);
  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: 0, behavior: 'smooth' });
    setShowJumpToBottom(false);
  }, []);

  // streaming 임시 메시지를 createdAt asc 정렬로 (시간 오름차순) 보관 — JSX 에서 reverse 렌더.
  const streamingList = useMemo<readonly StreamingMessage[]>(() => {
    if (!streamingMap) return EMPTY_STREAMS;
    const arr = Object.values(streamingMap);
    arr.sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0));
    return arr;
  }, [streamingMap]);

  // 채팅방 전환 시 visual bottom (최신 메시지) 으로 — column-reverse 에서 scrollTop = 0.
  // 별도 ResizeObserver / useAutoScroll 불필요 — 브라우저 native scroll-anchoring 이
  // 메시지 추가·입력창 grow·streaming chunk 모든 layout 변동에서 visual bottom 자동 유지.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || messages.length === 0) return;
    requestAnimationFrame(() => {
      if (containerRef.current) containerRef.current.scrollTop = 0;
    });
  }, [roomId, messages.length === 0]);

  const displayNames = useChatDisplayNameStore((st) => st.names);
  const memberMap = useMemo(() => {
    const m = new Map<string, RoomMemberDto>();
    for (const member of members) m.set(member.userId, member);
    return m;
  }, [members]);

  // ── chat-read-receipts ───────────────────────────────────────────────────────
  const myId = String(currentUserId ?? '');
  const readStates = useChatStore((s) => s.readStatesByRoom[roomId]);
  const unreadAnchor = useChatStore((s) => s.unreadAnchorByRoom[roomId]);
  const isDirect = isDirectRoom(roomType, memberCount ?? members.length);
  // "여기까지 읽었습니다" 구분선을 붙일 메시지 index. 방 진입 시점 앵커 기준 — 머무는 동안 고정.
  const firstUnreadIdx = useMemo(
    () => findFirstUnreadIndex(messages, unreadAnchor, myId),
    [messages, unreadAnchor, myId],
  );
  // 읽음 상태 라벨('읽음'/'모두 읽음')은 내 **마지막** 메시지에만 — 모든 말풍선에 붙이면 소음이다.
  // 미읽음 인원 숫자 배지는 해당 메시지마다 표시(누가 어디까지 봤는지가 메시지별로 다르므로).
  const lastMineIdx = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]!;
      if (!m.isAi && m.senderId != null && m.senderId === myId && !isSystemMessage(m)) return i;
    }
    return -1;
  }, [messages, myId]);
  const memberNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const member of members) m[member.userId] = member.name;
    return m;
  }, [members]);

  if (messages.length === 0 && streamingList.length === 0) {
    return (
      <div ref={containerRef} className="chat-message-list scrollbar-thin chat-message-list--empty" aria-label={tChat('chat.list.aria_log')} role="log">
        <p className="chat-message-list-empty-text">{tChat('chat.list.empty')}</p>
      </div>
    );
  }

  return (
    <div className="chat-message-list-wrap">
      <div ref={containerRef} onScroll={handleScroll} className="chat-message-list scrollbar-thin" aria-label={tChat('chat.list.aria_log')} role="log">
      {/* column-reverse 컨테이너 — DOM 순서가 visual 의 reverse. 시간 최근(visual bottom)
       * 부터 옛것(visual top) 순서로 DOM 에 배치. prev 평가는 array 의 original index 로
       * 변환하여 grouping 로직 유지. */}
      {aiPending && streamingList.length === 0 && <AiThinkingMessage tool={aiTool} />}
      {streamingList.slice().reverse().map((s, displayIdx) => {
        const idx = streamingList.length - 1 - displayIdx;
        const prevStream = streamingList[idx - 1];
        const lastMessage = messages[messages.length - 1];
        const prevIsAi = prevStream
          ? true
          : (lastMessage ? lastMessage.isAi && !isSystemMessage(lastMessage) : false);
        const showAvatar = !prevIsAi;
        return (
          <AiStreamingMessage
            key={`stream:${s.streamMessageId}`}
            streaming={s}
            showAvatar={showAvatar}
          />
        );
      })}
      {messages.slice().reverse().map((msg, displayIdx) => {
        const idx = messages.length - 1 - displayIdx;
        const prev = messages[idx - 1];
        // grouping break 조건: (1) prev 없음 (2) senderId 다름 (3) isAi 다름
        // (4) system 분기 다름 (system→user / user→system 전환)
        // (5) system 메시지는 항상 avatar 노출 — 룸 생성·멤버 합류 등 다른 이벤트가 같은
        //     senderId=null 로 묶이면 의미 손실. (review C1 봉합)
        const showAvatar = !prev
          || prev.senderId !== msg.senderId
          || prev.isAi !== msg.isAi
          || isSystemMessage(prev) !== isSystemMessage(msg)
          || isSystemMessage(msg);
        // 일자 구분선: msg 가 자기 날짜의 첫(가장 오래된) 메시지면 그 위에 divider.
        // prev(시간상 더 과거) 와 day 가 다르면 경계. prev 없으면(가장 오래된 로드분) 항상 경계.
        // column-reverse: divider 를 MessageItem 뒤(DOM) 에 두면 시각상 위쪽에 표시된다.
        const showDayDivider = dayKey(prev?.createdAt) !== dayKey(msg.createdAt);
        const dayLabel = showDayDivider ? formatDayLabel(msg.createdAt) : null;
        const isMe = !msg.isAi && msg.senderId !== null && msg.senderId === myId;
        const receipt = getReceipt({
          msg, isMe, idx, isDirect, lastMineIdx, readStates, myId, memberNameById,
          memberTotal: memberCount ?? members.length,
        });
        return (
          <Fragment key={msg.id}>
            <MessageItem
              message={msg}
              showAvatar={showAvatar}
              isMe={isMe}
              sender={getSenderInfo(msg, memberMap, displayNames)}
              unreadBy={receipt.unreadBy}
              readLabel={receipt.readLabel}
              readTitle={receipt.title}
            />
            {/* column-reverse — DOM 에서 MessageItem 뒤에 두면 시각상 그 *위* 에 표시된다.
              * 순서는 [메시지, 미읽음 구분선, 날짜 구분선] → 화면에서는 날짜 → 미읽음 → 메시지. */}
            {idx === firstUnreadIdx && (
              <div className="chat-message-list-unread-divider" role="separator">
                <span>{tChat('chat.read.divider')}</span>
              </div>
            )}
            {dayLabel && (
              <div className="chat-message-list-day-divider">
                <span>{dayLabel}</span>
              </div>
            )}
          </Fragment>
        );
      })}
      {/* visual top (DOM 끝) — 이전 대화 불러오기 액션. hasMoreHistory 일 때만. */}
      {hasMoreHistory && (
        <div className="chat-message-list-load-older">
          <button
            type="button"
            className="chat-load-older-btn"
            onClick={() => onLoadOlder?.(roomId)}
            disabled={loadingOlder}
          >
            {loadingOlder ? tChat('chat.list.loading_older') : tChat('chat.list.load_older')}
          </button>
        </div>
      )}
      </div>
      {showJumpToBottom && (
        <button
          type="button"
          className="chat-scroll-bottom"
          onClick={scrollToBottom}
          aria-label={tChat('chat.list.scroll_bottom_aria')}
        >
          <ChevronDown size={20} />
        </button>
      )}
    </div>
  );
}

const EMPTY: readonly ChatMessageEvent[] = [];
const EMPTY_STREAMS: readonly StreamingMessage[] = [];

/** '/ai …' 로 시작하는 사용자 메시지 — 다인 방에서는 발신자 전용(대화상대 미노출)이라 읽음 표시 대상이 아니다. */
const AI_PROMPT_RE = /^\/ai(\s|$)/;

interface ReceiptArgs {
  msg: ChatMessageEvent;
  isMe: boolean;
  idx: number;
  isDirect: boolean;
  lastMineIdx: number;
  readStates: Record<string, string | null> | undefined;
  myId: string;
  memberNameById: Record<string, string>;
  memberTotal: number;
}

interface Receipt {
  /** 아직 안 읽은 인원. -1 = 표시 안 함(내 메시지가 아니거나 아직 상태를 모름). */
  unreadBy: number;
  /** 전원이 읽었을 때 내 마지막 메시지에만 붙는 라벨 ('읽음' / '모두 읽음'). */
  readLabel: string | null;
  /** 그룹에서 누가 안 읽었는지 — 배지 hover 툴팁. */
  title: string | null;
}

/**
 * 메시지 하나의 읽음 표시 계산 (chat-read-receipts).
 *
 * 표시 대상은 **내가 보낸 일반 메시지** 뿐이다. 남의 메시지 옆에 "몇 명 안 읽음" 을 띄우는
 * 메신저는 없고, 시스템/AI/`/ai` 프롬프트는 애초에 읽음 개념이 성립하지 않는다
 * (특히 `/ai` 는 다인 방에서 발신자에게만 보이므로 영원히 '안 읽음' 으로 남는다).
 */
function getReceipt(a: ReceiptArgs): Receipt {
  const none: Receipt = { unreadBy: -1, readLabel: null, title: null };
  if (!a.isMe || a.msg.isAi || isSystemMessage(a.msg)) return none;
  if (AI_PROMPT_RE.test(a.msg.content)) return none;
  if (a.memberTotal <= 1) return none;   // 나 혼자인 방(AI 전용 등) — 읽을 상대가 없다.

  const unreadBy = countUnreadMembers({
    messageId: a.msg.id,
    readStates: a.readStates,
    myUserId: a.myId,
  });
  if (unreadBy < 0) return none;         // 아직 read-states 미조회 — 섣불리 '읽음' 이라 하지 않는다.
  if (unreadBy === 0) {
    return {
      unreadBy: 0,
      readLabel: a.idx === a.lastMineIdx
        ? tChat(a.isDirect ? 'chat.read.seen_direct' : 'chat.read.seen_group')
        : null,
      title: null,
    };
  }
  // 그룹에서만 "누가 안 읽었는지" 를 툴팁으로 — 1:1 은 상대가 한 명이라 자명하다.
  const title = a.isDirect
    ? tChat('chat.read.unread_one_aria')
    : tChat('chat.read.unread_names', {
        names: listUnreadUserIds({ messageId: a.msg.id, readStates: a.readStates, myUserId: a.myId })
          .map((uid) => a.memberNameById[uid] ?? uid)
          .join(', '),
      });
  return { unreadBy, readLabel: null, title };
}

interface SenderInfo {
  name: string;
  jobTitle: string | null;
  /** 표시용 아바타 src — wcavatar 센티널 해석 완료, 없으면 이니셜 폴백 (2026-06-10 additive). */
  avatarSrc?: string | null;
}

function getSenderInfo(
  msg: ChatMessageEvent,
  memberMap: Map<string, RoomMemberDto>,
  displayNames: Record<string, string> = {},
): SenderInfo {
  if (msg.isAi || msg.senderId === null) {
    return { name: tChat('chat.item.ai_assistant'), jobTitle: null };
  }
  const m = memberMap.get(msg.senderId);
  // 내가 지은 이름이 있으면 그것으로 부른다 — 정보 패널·방 목록과 같은 이름이어야 한다.
  const mine = myNameFor(displayNames, 'user', msg.senderId);
  return {
    name: mine ?? m?.name ?? tChat('chat.list.sender_unknown'),
    jobTitle: m?.jobTitle ?? null,
    avatarSrc: m ? avatarImageUrl(m.userId, m.avatarUrl) : null,
  };
}

/** 로컬 달력일 키 — 같은 날이면 동일 문자열. divider 경계 판정용 (undefined/invalid 는 ''). */
function dayKey(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toDateString();
}

function formatDayLabel(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const md = tChat('chat.list.day.md_ko', { month: d.getMonth() + 1, day: d.getDate() });
  if (sameDay) return tChat('chat.list.day.today', { md });
  if (isYesterday) return tChat('chat.list.day.yesterday', { md });
  return md;
}
